import { useEffect, useMemo } from "react";
import Container from "@mui/material/Container";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import DelayedLinearProgress from "@/components/DelayedLinearProgress";
import { PerfumeCategoryTitle } from "@/components/PerfumeCategoryTitle";
import {
  PageLink,
  allAvailableIngredients,
  makeIngredient,
  perfumeUserFullname,
  perfumeVolume,
  userIsAdmin,
  PERFUME_EDIT_WINDOW_MS,
  ComponentCategory,
} from "@/models";
import { useNow } from "@/hooks/useNow";
import LockOpenOutlined from "@mui/icons-material/LockOpenOutlined";
import { services } from "@/injectServices";
import { renderLocalDatetime, useRequiredParam } from "@/components";
import { useLoading } from "@/hooks/Loading";
import { useNavigate } from "react-router-dom";
import {
  deletePerfume,
  useAuthentication,
  useIngredientDiplayName,
  useTrackPerfume,
} from "@/services";
import { FormulaNumber } from "@/components/formulaNumber";
import { useTranslation } from "react-i18next";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

export function PerfumeDetailsPage() {
  const { t } = useTranslation();
  useEffect(() => {
    document.title = `Galimard Workshop: Perfume details`;
  });
  const perfumeId = useRequiredParam("perfumeId");

  const [pageLoading, pageLoadingDispatcher] = useLoading();

  const navigate = useNavigate();

  const { perfume } = useTrackPerfume({
    service: services().perfumeService,
    loadingDispatcher: pageLoadingDispatcher,
    perfumeId,
  });

  const handleEditPerfume = () => {
    if (perfume !== null) {
      navigate(
        PageLink.perfumeWorkshop({
          perfumeId: perfume.recipeId,
        }),
      );
    }
  };

  // let the client edit their formula again (only during the edit window, see firestore.rules)
  const handleUnlockPerfume = async () => {
    await services().perfumeService.perfumeRepository.unlockPerfume(perfumeId);
  };

  const handleDeletePerfume = async () => {
    const name = perfume?.name ? `'${perfume.name}'` : "";
    const author =
      perfume?.username || perfume?.email
        ? t("detailsDeleteConfirmationAuthor", {
            author: `${perfumeUserFullname(perfume)} ${perfume.email}`,
          })
        : "";
    const confirmationMessage = t("detailsDeleteConfirmation", { perfume: `${name} ${author}` });
    if (window.confirm(confirmationMessage)) {
      await deletePerfume(services().perfumeService, pageLoadingDispatcher, perfumeId);
      navigate(PageLink.Perfumes);
    }
  };

  const compositionByCategory = useMemo(() => {
    const allComponents = (perfume?.components || [])
      .map((component) => {
        const [ingredient] = makeIngredient(component, allAvailableIngredients, {
          createMissingIngredient: true,
        });
        return {
          component,
          ingredient,
        };
      })
      .filter((c) => c.ingredient !== null);

    const filterByCategory = (category: ComponentCategory) =>
      allComponents.filter((c) => c.component.category === category);

    return {
      [ComponentCategory.NOTE_DE_TETE]: filterByCategory(ComponentCategory.NOTE_DE_TETE),
      [ComponentCategory.NOTE_DE_COEUR]: filterByCategory(ComponentCategory.NOTE_DE_COEUR),
      [ComponentCategory.NOTE_DE_FOND]: filterByCategory(ComponentCategory.NOTE_DE_FOND),
    };
  }, [perfume?.components]);

  const { user } = useAuthentication(services().authenticationService);
  const isAdmin = userIsAdmin(user);

  // after the edit window, unlocking is useless: the client can't edit anyway
  const now = useNow(60_000);
  const canUnlock =
    perfume !== null &&
    perfume.lockedAt !== null &&
    now.getTime() - perfume.created.getTime() < PERFUME_EDIT_WINDOW_MS;

  const ingredientDisplayName = useIngredientDiplayName();

  return (
    <Container>
      <DelayedLinearProgress loading={pageLoading} />
      {perfume && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {t("detailsPerfume")} <FormulaNumber formulaNumber={perfume.formulaNumber} />
            </Typography>

            <Typography variant="h5" component="div">
              {perfume.name}
            </Typography>

            {perfume.username && (
              <Typography variant="h6" component="div">
                {t("detailsCreatedBy", { author: perfumeUserFullname(perfume) })}
              </Typography>
            )}

            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              {perfume.email}
            </Typography>

            <Typography variant="body1" color="text.secondary" gutterBottom>
              {perfume.workshopLocation
                ? t("detailsCreatedOnAt", {
                    date: renderLocalDatetime(perfume.created),
                    location: perfume.workshopLocation.address,
                  })
                : t("detailsCreatedOn", { date: renderLocalDatetime(perfume.created) })}
            </Typography>

            {perfume.lockedAt && (
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {t("detailsLockedOn", { date: renderLocalDatetime(perfume.lockedAt) })}
              </Typography>
            )}

            {(
              [
                ComponentCategory.NOTE_DE_TETE,
                ComponentCategory.NOTE_DE_COEUR,
                ComponentCategory.NOTE_DE_FOND,
              ] as const
            ).map((category) => {
              const composition = compositionByCategory[category];
              if (composition.length === 0) return null;
              return (
                <TableContainer component={Paper} key={category} sx={{ my: 2 }}>
                  <Table sx={{ maxWidth: 400 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell colSpan={3}>
                          <PerfumeCategoryTitle category={category} quantity={composition.length} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t("detailsIngredientName")}</TableCell>
                        <TableCell align="right">{t("detailsGalimardReference")}</TableCell>
                        <TableCell align="right">{t("detailsLotNumber")}</TableCell>
                        <TableCell align="right">{t("detailsVolume")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {composition.map(({ component, ingredient }, i) => (
                        <TableRow
                          key={i}
                          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            {ingredientDisplayName(ingredient)}
                          </TableCell>
                          <TableCell align="right">#{ingredient?.galimardReference}</TableCell>
                          <TableCell align="right">{component.numeroDeLot ?? "—"}</TableCell>
                          <TableCell align="right">{component.volume}&nbsp;ml</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })}

            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t("detailsTotal")} {perfumeVolume(perfume)}&nbsp;ml
            </Typography>

            {isAdmin && (
              <CardActions>
                <Button size="small" onClick={handleEditPerfume}>
                  {t("detailsEdit")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LockOpenOutlined />}
                  disabled={!canUnlock}
                  onClick={handleUnlockPerfume}
                >
                  {t("detailsUnlock")}
                </Button>
                <Button color="danger" size="small" onClick={handleDeletePerfume}>
                  {t("delete")}
                </Button>
              </CardActions>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
